import { useMemo } from 'react';

import { FieldSyncRule } from '@/modules/projects/components/modals/resolve-conflicts-modal';
import { ProjectStatus } from '@/modules/projects/types/project';
import { ConflictSnapshot } from '@/modules/projects/types/project-conflicts';

/**
 * Returns field sync rules for the project info conflict resolution modal.
 *
 * Rules:
 * 1. status → when not Tentative, clear probability (active projects have no probability).
 *    When switched back to Tentative, restore the original form value or fall back to the
 *    first available non-null probability from the conflict snapshots.
 *
 * @param originalProbability - The probability value from the tab's form at the time the modal opens.
 * @param snapshots           - Conflict snapshots used to find a fallback probability.
 */
export const useProjectInfoSyncRules = (
  originalProbability: number | null | undefined,
  snapshots: ConflictSnapshot[]
): FieldSyncRule[] => {
  return useMemo(
    (): FieldSyncRule[] => [
      {
        watchField: 'status',
        applyOnMount: true,
        onValueChange: (status, setFieldValue) => {
          if (!status) return;

          if (status !== ProjectStatus.Tentative) {
            // Active / Archived projects have no probability
            setFieldValue('probability', null);
          } else {
            // Restore original probability, or fall back to the first snapshot value
            if (originalProbability !== null && originalProbability !== undefined) {
              setFieldValue('probability', originalProbability);
            } else {
              const firstSnapshotProbability =
                snapshots
                  .map(s => {
                    const raw = s.data['tentativeProbability'];
                    return raw !== null && raw !== undefined ? Math.round(raw as number) : null;
                  })
                  .find(v => v !== null) ?? null;

              setFieldValue('probability', firstSnapshotProbability);
            }
          }
        },
      },
    ],
    []
  );
};
